import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  TextField,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

const STATUS_STYLES = {
  'Hakediş Oluştu': { color: '#1597D3', backgroundColor: '#E7F7FF' },
  'Fatura Yüklendi': { color: '#47A447', backgroundColor: '#EAF8E8' },
  'Kontrol Edildi': { color: '#7A5AF8', backgroundColor: '#F4F3FF' },
  'Fatura Ödendi': { color: '#FFFFFF', backgroundColor: '#48A23F' },
  'Fatura Reddedildi': { color: '#E14A3B', backgroundColor: '#FFE8E5' },
  'Gecikmiş Fatura': { color: '#DD6B20', backgroundColor: '#FFF0E1' },
  'Devreden Hakediş': { color: '#B545D4', backgroundColor: '#F8E5FF' },
};

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}₺`;

const normalizeDateValue = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }

  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const numericValue = Number(value);
    if (String(Math.trunc(numericValue)).length === 10) {
      return dayjs.unix(numericValue);
    }
    if (String(Math.trunc(numericValue)).length === 13) {
      return dayjs(numericValue);
    }
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const parseCurrencyValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value || '')
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^0-9.-]/g, '');

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyInput = (value) => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return '';
  }

  return String(value).replace('.', ',');
};

const normalizeCutOff = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => ({
    cutOffID: item?.cutOffID || '',
    amount: Number(item?.amount || 0),
    date: item?.date || '',
    createdBy: item?.createdBy || '',
  }));
};

const ADDITIONAL_PAYMENT_OPTIONS = [
  'Avon Temsilci Aday Başvurusu',
  'İndirim Kodu',
  'Digitürk Dolu Dolu TV Paketi',
  'Digitürk Öğrenci Paketi',
  'Digitürk TV',
  'Digitürk TV + İnternet',
  'Diğer',
];

const normalizeAdditionalPayments = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => ({
    additionalPaymentID:
      item?.additionalPaymentID || item?.additional_payment_id || item?.id || '',
    amount: Number(item?.amount || 0),
    additionalNote: item?.additionalNote || item?.note || '',
    date: item?.date || item?.createdDate || item?.createdDateTime || '',
    createdBy: item?.createdBy || '',
  }));
};

const getMonthRange = (invoiceDate) => {
  if (!invoiceDate) {
    return { startDate: null, endDate: null };
  }

  const monthStart = dayjs(`${invoiceDate}-01`).startOf('month');
  return {
    startDate: monthStart.format('YYYY-MM-DD'),
    endDate: monthStart.endOf('month').format('YYYY-MM-DD'),
  };
};

function StatusChip({ label }) {
  const style = STATUS_STYLES[label] || STATUS_STYLES['Hakediş Oluştu'];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.25,
        minHeight: 22,
        borderRadius: '999px',
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: style.color,
        backgroundColor: style.backgroundColor,
      }}
    >
      {label}
    </Box>
  );
}

export default function HakedisManagementDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [compensationAmount, setCompensationAmount] = useState('');
  const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState('');
  const [selectedAdditionalPaymentType, setSelectedAdditionalPaymentType] = useState('');
  const [showCutOffDetails, setShowCutOffDetails] = useState(true);
  const [showAdditionalPaymentDetails, setShowAdditionalPaymentDetails] = useState(true);
  const [cutOffToRemove, setCutOffToRemove] = useState(null);
  const [additionalPaymentModalOpen, setAdditionalPaymentModalOpen] = useState(false);
  const [additionalPaymentToRemove, setAdditionalPaymentToRemove] = useState(null);
  const [removingCutOff, setRemovingCutOff] = useState(false);
  const [removingAdditionalPayment, setRemovingAdditionalPayment] = useState(false);

  const initialRow = useMemo(() => {
    if (location.state?.row) return location.state.row;
    const stored = sessionStorage.getItem(`hakedis-detail-${id}`);
    if (stored) return JSON.parse(stored);

    const localStored = localStorage.getItem(`hakedis-detail-${id}`);
    return localStored ? JSON.parse(localStored) : null;
  }, [id, location.state]);
  const [row, setRow] = useState(initialRow);
  const [cutOffEntries, setCutOffEntries] = useState([]);
  const [additionalPaymentEntries, setAdditionalPaymentEntries] = useState([]);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const invoiceUploadInputRef = useRef(null);

  useEffect(() => {
    setRow(initialRow);
  }, [initialRow]);

  useEffect(() => {
    setCutOffEntries(normalizeCutOff(row?.cutOff || row?.rawItem?.cutOff));
    setAdditionalPaymentEntries(
      normalizeAdditionalPayments(
        row?.additionalPayment ||
          row?.additionalPayments ||
          row?.rawItem?.additionalPayment ||
          row?.rawItem?.additionalPayments
      )
    );
  }, [row]);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!row?.pointId || !row?.month) {
        setSummary(null);
        return;
      }

      const { startDate, endDate } = getMonthRange(row.month);
      if (!startDate || !endDate) {
        setSummary(null);
        return;
      }

      setLoading(true);
      setErrorText('');
      try {
        const response = await Services.getProdPrims(startDate, endDate, row.pointId);
        const result = response?.data?.result;
        if (!response?.data?.status || !result) {
          throw new Error('Hakediş detayı bulunamadı.');
        }

        const counts = {
          trendyolCount: Number(result?.trendyol || 0),
          hepsiburadaCount: Number(result?.hepsiburada || 0),
          hepsijetCount: Number(result?.hepsijet || 0),
          arasCount: Number(result?.araskargo || 0),
          kargoistCount: Number(result?.kargoist || 0),
          dhlCount: Number(result?.dhl || 0),
          othersCount: Number(result?.others || 0),
        };
        const totalCount = Object.values(counts).reduce((sum, value) => sum + Number(value || 0), 0);
        const amountWithoutTax = Number(result?.['prim-without-tax'] || 0);
        const tax = Number(result?.tax || 0);
        const amountWithTax = Number(result?.['prim-with-tax'] || row?.amount || 0);

        setSummary({
          ...counts,
          totalCount,
          unitPrice: totalCount > 0 ? amountWithoutTax / totalCount : 0,
          amountWithoutTax,
          tax,
          amountWithTax,
        });
      } catch (error) {
        console.error('[HakedisManagementDetailPage] getPrims error', error);
        setSummary(null);
        setErrorText('Hakediş detayı alınırken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [row?.amount, row?.month, row?.pointId]);

  const totalCutOffAmount = cutOffEntries.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const totalAdditionalPaymentAmount = additionalPaymentEntries.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );
  const additionalPaymentSummaryRows = Object.values(
    additionalPaymentEntries.reduce((accumulator, item) => {
      const serviceLabel = item.additionalNote || 'Diğer';
      if (!accumulator[serviceLabel]) {
        accumulator[serviceLabel] = {
          label: serviceLabel,
          count: 0,
          amount: 0,
        };
      }

      accumulator[serviceLabel].count += 1;
      accumulator[serviceLabel].amount += Number(item.amount || 0);
      return accumulator;
    }, {})
  ).map((item) => ({
    label:
      item.count > 1
        ? `Ek tutar ${item.label} (${item.count})`
        : `Ek tutar ${item.label}`,
    value: formatCurrency(item.amount),
  }));
  const baseAmountWithoutTax = Number(summary?.amountWithoutTax || 0);
  const baseAmountWithTax = Number(summary?.amountWithTax || 0);
  const adjustedTotalAmount = Math.max(
    baseAmountWithTax - totalCutOffAmount + totalAdditionalPaymentAmount,
    0
  );

  const detailRows = [
    {
      label: 'Fatura Durumu',
      value: (
        <Stack direction="row" alignItems="center" spacing={1}>
          <StatusChip label={row?.invoiceStatus || 'Hakediş Oluştu'} />
          {row?.invoiceFileURL && row?.invoiceStatus === 'Fatura Yüklendi' ? (
            <Button
              component="a"
              href={row.invoiceFileURL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ minWidth: 24, width: 24, p: 0, color: '#8F96A3' }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
            </Button>
          ) : null}
        </Stack>
      ),
    },
    { label: 'Trendyol Paket Adedi', value: summary?.trendyolCount ?? '-' },
    { label: 'Hepsiburada Paket Adedi', value: summary?.hepsiburadaCount ?? '-' },
    { label: 'Hepsijet Paket Adedi', value: summary?.hepsijetCount ?? '-' },
    { label: 'Aras Kargo Paket Adedi', value: summary?.arasCount ?? '-' },
    { label: 'CargoX Paket Adedi', value: summary?.kargoistCount ?? '-' },
    { label: 'DHL Paket Adedi', value: summary?.dhlCount ?? '-' },
    { label: 'Diğer Paket Adedi', value: summary?.othersCount ?? '-' },
    { label: 'Toplam Adet', value: summary?.totalCount ?? '-' },
    { label: 'Birim Fiyat', value: summary ? formatCurrency(summary.unitPrice) : '-' },
    { label: 'KDV Hariç', value: summary ? formatCurrency(baseAmountWithoutTax) : '-' },
    { label: 'KDV Dahil (%20)', value: summary ? formatCurrency(baseAmountWithTax) : '-' },
    {
      label: 'Yüklenme Tarihi',
      value: normalizeDateValue(row?.uploadDate)?.locale('tr').format('DD.MM.YYYY') || '-',
    },
    ...(totalCutOffAmount > 0 ? [{ label: 'Tazmin Tutarı', value: formatCurrency(totalCutOffAmount) }] : []),
    ...additionalPaymentSummaryRows,
    { label: 'Toplam Tutar', value: formatCurrency(adjustedTotalAmount), strong: true },
  ];

  const handleApprove = async () => {
    const parsedCompensationAmount = parseCurrencyValue(compensationAmount);
    const invoiceID = row?.invoiceID || row?.rawItem?.invoice_id || row?.rawItem?.invoiceID;

    if (parsedCompensationAmount < 0) {
      toast.error('Tazmin tutarı negatif olamaz.');
      return;
    }

    if (!invoiceID) {
      toast.error('Fatura numarası bulunamadı.');
      return;
    }

    setSubmitting(true);
    try {
      await Services.SetEsnafCutoff(invoiceID, parsedCompensationAmount);
      setCutOffEntries((current) => [
        ...current,
        {
          amount: parsedCompensationAmount,
          date: dayjs().format('YYYY-MM-DD HH:mm'),
        },
      ]);
      setCompensationAmount('');
      toast.success('Tazmin tutarı başarıyla kaydedildi.');
      navigate('/dashboard/hakedis-management');
    } catch (error) {
      console.error('[HakedisManagementDetailPage] set cutoff error', error);
      toast.error('Tazmin tutarı kaydedilirken bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveCutOff = async () => {
    if (!cutOffToRemove?.cutOffID) {
      toast.error('Silinecek tazmin kaydı bulunamadı.');
      return;
    }

    setRemovingCutOff(true);
    try {
      await Services.RemoveEsnafCutoff(cutOffToRemove.cutOffID);
      setCutOffEntries((current) =>
        current.filter((item) => item.cutOffID !== cutOffToRemove.cutOffID)
      );
      setCutOffToRemove(null);
      toast.success('Tazmin kaydı başarıyla silindi.');
    } catch (error) {
      console.error('[HakedisManagementDetailPage] remove cutoff error', error);
      toast.error('Tazmin kaydı silinirken bir hata oluştu.');
    } finally {
      setRemovingCutOff(false);
    }
  };

  const handleAddAdditionalPayment = async () => {
    const parsedAmount = parseCurrencyValue(additionalPaymentAmount);
    const invoiceID = row?.invoiceID || row?.rawItem?.invoice_id || row?.rawItem?.invoiceID;

    if (!selectedAdditionalPaymentType) {
      toast.error('Ek tutar tipi seçmelisiniz.');
      return;
    }

    if (parsedAmount <= 0) {
      toast.error("Ek tutar 0'dan büyük olmalı.");
      return;
    }

    if (!invoiceID) {
      toast.error('Fatura numarası bulunamadı.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await Services.AddFinancialAdditionalPayment(
        invoiceID,
        parsedAmount,
        selectedAdditionalPaymentType,
        selectedAdditionalPaymentType
      );
      const result = response?.data?.result;

      setAdditionalPaymentEntries((current) => [
        ...current,
        {
          additionalPaymentID:
            result?.additionalPaymentID || result?.additional_payment_id || result?.id || '',
          amount: parsedAmount,
          additionalNote: selectedAdditionalPaymentType,
          date: result?.date || result?.createdDate || dayjs().format('YYYY-MM-DD HH:mm'),
          createdBy: result?.createdBy || '',
        },
      ]);
      setSelectedAdditionalPaymentType('');
      setAdditionalPaymentAmount('');
      setAdditionalPaymentModalOpen(false);
      toast.success('Ek tutar başarıyla kaydedildi.');
    } catch (error) {
      console.error('[HakedisManagementDetailPage] add additional payment error', error);
      toast.error('Ek tutar kaydedilirken bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAdditionalPayment = async () => {
    if (!additionalPaymentToRemove?.additionalPaymentID) {
      toast.error('Silinecek ek tutar kaydı bulunamadı.');
      return;
    }

    setRemovingAdditionalPayment(true);
    try {
      await Services.RemoveFinancialAdditionalPayment(
        additionalPaymentToRemove.additionalPaymentID
      );
      setAdditionalPaymentEntries((current) =>
        current.filter(
          (item) => item.additionalPaymentID !== additionalPaymentToRemove.additionalPaymentID
        )
      );
      setAdditionalPaymentToRemove(null);
      toast.success('Ek tutar kaydı başarıyla silindi.');
    } catch (error) {
      console.error('[HakedisManagementDetailPage] remove additional payment error', error);
      toast.error('Ek tutar kaydı silinirken bir hata oluştu.');
    } finally {
      setRemovingAdditionalPayment(false);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64Content = result.includes(',')
          ? result.split(',').pop()
          : result;
        resolve(base64Content);
      };
      reader.onerror = () => reject(reader.error || new Error('Dosya okunamadı.'));
      reader.readAsDataURL(file);
    });

  const handleInvoiceUploadButtonClick = () => {
    if (!row?.invoiceID || !row?.pointId || !row?.month || uploadingInvoice) {
      return;
    }

    invoiceUploadInputRef.current?.click();
  };

  const handleInvoiceFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      event.target.value = '';
      return;
    }

    try {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension) {
        throw new Error('Dosya uzantısı bulunamadı.');
      }

      setUploadingInvoice(true);
      const base64File = await toBase64(file);
      const response = await Services.FinancialUploadEsnafInvoice(
        row.pointId,
        row.month,
        base64File,
        extension,
        row.invoiceID
      );

      if (!response?.data?.status) {
        throw new Error(response?.data?.message || 'Fatura yüklenemedi.');
      }

      const nextUploadDate = dayjs().format('YYYY-MM-DD HH:mm:ss');
      const nextInvoiceFileURL =
        response?.data?.result?.invoiceFileURL || row?.invoiceFileURL || null;
      const nextRow = {
        ...row,
        invoiceStatus: 'Fatura Yüklendi',
        uploadStatus: 'Yüklendi',
        uploadDate: nextUploadDate,
        invoiceFileURL: nextInvoiceFileURL,
        rawItem: {
          ...row?.rawItem,
          invoiceFileURL: nextInvoiceFileURL,
          uploadDate: nextUploadDate,
          status: '4',
        },
      };

      setRow(nextRow);
      sessionStorage.setItem(`hakedis-detail-${nextRow.id}`, JSON.stringify(nextRow));
      localStorage.setItem(`hakedis-detail-${nextRow.id}`, JSON.stringify(nextRow));
      toast.success('Fatura başarıyla yüklendi.');
    } catch (error) {
      console.error('[HakedisManagementDetailPage] invoice upload error', error);
      toast.error(error?.message || 'Fatura yüklenirken bir hata oluştu.');
    } finally {
      setUploadingInvoice(false);
      event.target.value = '';
    }
  };

  return (
    <Box sx={{ px: { xs: 2, lg: 5 }, pb: 5 }}>
      <Helmet>
        <title>Hakediş Detayı</title>
      </Helmet>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2.5, mt: 1 }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#2A2E3F' }}>
          Hakediş Yönetimi
        </Typography>
        <Stack direction="row" spacing={1}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            ref={invoiceUploadInputRef}
            style={{ display: 'none' }}
            onChange={handleInvoiceFileChange}
          />
          <Button
            variant="outlined"
            onClick={handleInvoiceUploadButtonClick}
            disabled={uploadingInvoice || !row}
            startIcon={
              uploadingInvoice ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              height: 36,
              borderRadius: '8px',
              borderColor: '#E5E7EB',
              color: '#4B5563',
              '&:hover': { backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' },
            }}
          >
            Fatura Yükle
          </Button>
          <Button
            onClick={() => navigate('/dashboard/hakedis-management')}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              p: 0,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              color: '#98A2B3',
            }}
          >
            <ChevronLeftRoundedIcon />
          </Button>
        </Stack>
      </Stack>

      {errorText ? (
        <Alert severity="warning" sx={{ maxWidth: 820, mx: 'auto', mb: 2, borderRadius: '10px' }}>
          {errorText}
        </Alert>
      ) : null}

      <Box
        sx={{
          maxWidth: 820,
          mx: 'auto',
          mb: 1.5,
          px: 2.5,
          py: 1.25,
          borderRadius: '8px',
          backgroundColor: '#E24337',
          color: '#FFFFFF',
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {row?.pointName || '-'}
      </Box>

      <Paper
        elevation={0}
        sx={{
          maxWidth: 820,
          mx: 'auto',
          borderRadius: '14px',
          border: '1px solid #EEF0F4',
          boxShadow: '0 10px 30px rgba(18, 38, 63, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        >
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#2A2E3F' }}>
              Hakediş Detayı
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#8B93A6', mt: 0.5 }}>
              Seçmiş olduğunuz hakediş ile ilgili detaylar aşağıdadır
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
              {row?.invoiceID || '-'}
            </Typography>
            <Button
              onClick={() => {
                if (row?.invoiceID) {
                  navigator.clipboard?.writeText(String(row.invoiceID));
                  toast.success('Hakediş numarası kopyalandı.');
                }
              }}
              sx={{ minWidth: 20, p: 0, color: '#98A2B3' }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ px: 2, pb: 2.5 }}>
            {detailRows.map((item, index) => (
              <Stack
                key={item.label}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '6px',
                  backgroundColor: index % 2 === 0 ? '#F7F7F7' : '#FFFFFF',
                }}
              >
                <Typography sx={{ color: '#2A2E3F', fontSize: 14, fontWeight: item.strong ? 600 : 500 }}>
                  {item.label}
                </Typography>
                {typeof item.value === 'string' || typeof item.value === 'number' ? (
                  <Typography sx={{ color: '#4B5563', fontSize: 14, fontWeight: item.strong ? 700 : 400 }}>
                    {item.value}
                  </Typography>
                ) : (
                  item.value
                )}
              </Stack>
            ))}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mt: 3, px: 1 }}
            >
              <Typography
                sx={{
                  minWidth: 96,
                  color: '#2A2E3F',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Tazmin Tutarı:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formatCurrencyInput(compensationAmount)}
                onChange={(event) => {
                  const nextValue = event.target.value.replace(/[^0-9,]/g, '');
                  const commaCount = (nextValue.match(/,/g) || []).length;
                  if (commaCount <= 1) {
                    setCompensationAmount(nextValue);
                  }
                }}
                placeholder="0,00"
                inputProps={{ inputMode: 'decimal', 'aria-label': 'Tazmin Tutarı' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                  },
                }}
              />
              <Button
                variant="contained"
                disableElevation
                onClick={handleApprove}
                disabled={submitting || !row}
                sx={{
                  minWidth: 104,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: '#E24337',
                  '&:hover': { backgroundColor: '#CC3A2E' },
                }}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Onayla'}
              </Button>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="flex-end"
              sx={{ mt: 2, px: 1 }}
            >
              <Button
                variant="outlined"
                onClick={() => setAdditionalPaymentModalOpen(true)}
                disabled={!row}
                sx={{
                  minWidth: 160,
                  height: 40,
                  borderRadius: '8px',
                  borderColor: '#E24337',
                  color: '#E24337',
                  '&:hover': { borderColor: '#CC3A2E', backgroundColor: '#FFF5F4' },
                }}
              >
                Ek tutar ekle
              </Button>
            </Stack>

            {cutOffEntries.length > 0 ? (
              <Box sx={{ mt: 1.5, px: 1 }}>
                <Button
                  variant="text"
                  onClick={() => setShowCutOffDetails((current) => !current)}
                  sx={{
                    width: '100%',
                    px: 0,
                    color: '#E24337',
                    fontSize: 13,
                    fontWeight: 700,
                    justifyContent: 'space-between',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  <Box component="span">Tazmin Detayları</Box>
                  {showCutOffDetails ? (
                    <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </Button>

                <Collapse in={showCutOffDetails} timeout={220}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: '10px',
                      border: '1px solid #EEF0F4',
                      backgroundColor: '#FAFBFC',
                    }}
                  >
                    {cutOffEntries.map((item, index) => (
                      <Stack
                        key={item.cutOffID || `${item.date}-${index}`}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          py: 0.75,
                          borderBottom: index === cutOffEntries.length - 1 ? 'none' : '1px solid #EEF0F4',
                        }}
                      >
                        <Typography sx={{ color: '#4B5563', fontSize: 13 }}>
                          {[item.date, item.createdBy].filter(Boolean).join(' - ') || '-'}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Button
                            onClick={() => setCutOffToRemove(item)}
                            sx={{
                              minWidth: 20,
                              width: 20,
                              height: 20,
                              p: 0,
                              color: '#E24337',
                            }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 16 }} />
                          </Button>
                          <Typography sx={{ color: '#2A2E3F', fontSize: 13, fontWeight: 600 }}>
                            {formatCurrency(item.amount)}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ) : null}

            {additionalPaymentEntries.length > 0 ? (
              <Box sx={{ mt: 1.5, px: 1 }}>
                <Button
                  variant="text"
                  onClick={() => setShowAdditionalPaymentDetails((current) => !current)}
                  sx={{
                    width: '100%',
                    px: 0,
                    color: '#1597D3',
                    fontSize: 13,
                    fontWeight: 700,
                    justifyContent: 'space-between',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  <Box component="span">Ek Tutar Detayları</Box>
                  {showAdditionalPaymentDetails ? (
                    <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </Button>

                <Collapse in={showAdditionalPaymentDetails} timeout={220}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: '10px',
                      border: '1px solid #EEF0F4',
                      backgroundColor: '#FAFBFC',
                    }}
                  >
                    {additionalPaymentEntries.map((item, index) => (
                      <Stack
                        key={item.additionalPaymentID || `${item.additionalNote}-${item.date}-${index}`}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          py: 0.75,
                          borderBottom:
                            index === additionalPaymentEntries.length - 1
                              ? 'none'
                              : '1px solid #EEF0F4',
                        }}
                      >
                        <Box>
                          <Typography sx={{ color: '#2A2E3F', fontSize: 13, fontWeight: 600 }}>
                            {item.additionalNote || '-'}
                          </Typography>
                          <Typography sx={{ color: '#4B5563', fontSize: 13 }}>
                            {[item.date, item.createdBy].filter(Boolean).join(' - ') || '-'}
                          </Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Button
                            onClick={() => setAdditionalPaymentToRemove(item)}
                            sx={{
                              minWidth: 20,
                              width: 20,
                              height: 20,
                              p: 0,
                              color: '#E24337',
                            }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 16 }} />
                          </Button>
                          <Typography sx={{ color: '#2A2E3F', fontSize: 13, fontWeight: 600 }}>
                            {formatCurrency(item.amount)}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ) : null}
          </Box>
        )}
      </Paper>

      <Dialog
        open={additionalPaymentModalOpen}
        onClose={() => (submitting ? null : setAdditionalPaymentModalOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ek Tutar Ekle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Select
              displayEmpty
              fullWidth
              size="small"
              value={selectedAdditionalPaymentType}
              onChange={(event) => setSelectedAdditionalPaymentType(event.target.value)}
            >
              <MenuItem value="" disabled>
                Seçiniz
              </MenuItem>
              {ADDITIONAL_PAYMENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth
              size="small"
              value={formatCurrencyInput(additionalPaymentAmount)}
              onChange={(event) => {
                const nextValue = event.target.value.replace(/[^0-9,]/g, '');
                const commaCount = (nextValue.match(/,/g) || []).length;
                if (commaCount <= 1) {
                  setAdditionalPaymentAmount(nextValue);
                }
              }}
              placeholder="Tutar"
              inputProps={{ inputMode: 'decimal', 'aria-label': 'Ek Tutar' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdditionalPaymentModalOpen(false)} disabled={submitting}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleAddAdditionalPayment}
            disabled={submitting}
            sx={{
              backgroundColor: '#E24337',
              '&:hover': { backgroundColor: '#CC3A2E' },
            }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cutOffToRemove)}
        onClose={() => (removingCutOff ? null : setCutOffToRemove(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Tazmin Kaydını Sil</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#4B5563' }}>
            Bu tazmin kaydını silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCutOffToRemove(null)} disabled={removingCutOff}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleRemoveCutOff}
            disabled={removingCutOff}
            sx={{
              backgroundColor: '#E24337',
              '&:hover': { backgroundColor: '#CC3A2E' },
            }}
          >
            {removingCutOff ? <CircularProgress size={18} color="inherit" /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(additionalPaymentToRemove)}
        onClose={() => (removingAdditionalPayment ? null : setAdditionalPaymentToRemove(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Ek Tutar Kaydını Sil</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#4B5563' }}>
            Bu ek tutar kaydını silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAdditionalPaymentToRemove(null)}
            disabled={removingAdditionalPayment}
          >
            Vazgeç
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleRemoveAdditionalPayment}
            disabled={removingAdditionalPayment}
            sx={{
              backgroundColor: '#E24337',
              '&:hover': { backgroundColor: '#CC3A2E' },
            }}
          >
            {removingAdditionalPayment ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Onayla'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
